import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Polls /api/chat/unread-summary and exposes the aggregated counter to the
// AppHeader (chat badge). Runs only when the caller is authenticated AND 18+
// — the endpoint returns 403 otherwise and we simply keep the counter at 0.

const ChatUnreadContext = createContext({
  total: 0, unreadMessages: 0, pendingRequests: 0, pendingInvites: 0, refresh: () => {},
});

export function useChatUnread() {
  return useContext(ChatUnreadContext);
}

export function ChatUnreadProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({ total: 0, unreadMessages: 0, pendingRequests: 0, pendingInvites: 0 });
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) { setState({ total: 0, unreadMessages: 0, pendingRequests: 0, pendingInvites: 0 }); return; }
    try {
      const { data } = await api.get("/chat/unread-summary");
      setState({
        total: data.total || 0,
        unreadMessages: data.unread_messages || 0,
        pendingRequests: data.pending_requests || 0,
        pendingInvites: data.pending_invites || 0,
      });
    } catch {
      // 403 for minors, 401 for anon — silent
    }
  }, [user]);

  useEffect(() => {
    refresh();
    if (timerRef.current) clearInterval(timerRef.current);
    if (user) timerRef.current = setInterval(refresh, 20000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [user, refresh]);

  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  return (
    <ChatUnreadContext.Provider value={{ ...state, refresh }}>
      {children}
    </ChatUnreadContext.Provider>
  );
}
